import { useNavigationTableSort } from "components/scrapEyeDataDashboard/navigation/useNavigationTableSort";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { SortableHeaderCell } from "components/common/SortableHeaderCell";
import { useNotifyStatus } from "contexts/status";
import { fetchErrorDetail } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { SortValueGetters } from "hooks/useTableSort";
import { useState } from "react";
import {
  ScrapEyeInstallationLocation,
  ScrapEyeRecordingPosition,
  useCreateInstallationLocationMutation,
  useCreateRecordingPositionMutation,
  useListInstallationLocationsQuery,
  useListRecordingPositionsQuery,
} from "src/store/api/generatedApi";
import { SupportingLocationAssociationSection } from "./SupportingLocationAssociationSection";

function formatPromiseRejectionReason(reason: unknown): string {
  const detail = fetchErrorDetail(reason);
  if (detail != null) {
    return detail;
  }
  if (reason instanceof Error) {
    return reason.message;
  }
  if (typeof reason === "string") {
    return reason;
  }
  if (
    typeof reason === "number" ||
    typeof reason === "boolean" ||
    typeof reason === "bigint"
  ) {
    return String(reason);
  }
  if (typeof reason === "symbol") {
    return reason.toString();
  }
  if (reason == null) {
    return String(reason);
  }
  try {
    return JSON.stringify(reason);
  } catch {
    return "Unknown error";
  }
}

const locationSortValueGetters: SortValueGetters<
  ScrapEyeInstallationLocation,
  "name" | "locationType"
> = {
  name: (location) => location.name,
  locationType: (location) => location.location_type,
};

const positionSortValueGetters: SortValueGetters<
  ScrapEyeRecordingPosition,
  "name" | "locationName"
> = {
  name: (position) => position.name,
  locationName: (position) => position.location_name,
};

export const InstallationConfiguration = () => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const notifyStatus = useNotifyStatus();
  const [locationName, setLocationName] = useState("");
  const [isAddPositionsToLocationOpen, setIsAddPositionsToLocationOpen] =
    useState(false);
  const [addPositionsDialogLocation, setAddPositionsDialogLocation] =
    useState("");
  const [pendingPositionNames, setPendingPositionNames] = useState<string[]>([
    "",
  ]);
  const { data: installationLocations, isLoading: isLocationsBusy } =
    useListInstallationLocationsQuery({ tenantName });
  const {
    data: recordingPositionsResponse,
    isLoading: isRecordingPositionsBusy,
  } = useListRecordingPositionsQuery({ tenantName });
  const [createLocation, { isLoading: isCreatingLocation }] =
    useCreateInstallationLocationMutation();
  const [createRecordingPosition, { isLoading: isCreatingRecordingPosition }] =
    useCreateRecordingPositionMutation();
  const locationNameOptions =
    installationLocations?.map((location) => location.name) ?? [];
  const recordingPositions = recordingPositionsResponse?.items ?? [];
  const {
    sortedRows: sortedInstallationLocations,
    sort: locationSort,
    toggleSort: toggleLocationSort,
  } = useNavigationTableSort({
    key: "locationOrder",
    rows: installationLocations,
    sortValueGetters: locationSortValueGetters,
    initialSort: null,
  });
  const {
    sortedRows: sortedRecordingPositions,
    sort: positionSort,
    toggleSort: togglePositionSort,
  } = useNavigationTableSort({
    key: "positionOrder",
    rows: recordingPositions,
    sortValueGetters: positionSortValueGetters,
    initialSort: null,
  });
  const handleCreateLocation = async () => {
    const trimmed = locationName.trim();
    if (trimmed.length === 0) {
      notifyStatus({
        type: "error",
        text: t("installationLocationCreateError"),
      });
      return;
    }

    try {
      await createLocation({
        tenantName,
        scrapEyeInstallationLocationCreate: {
          name: trimmed,
        },
      }).unwrap();
      notifyStatus({
        type: "success",
        text: t("installationLocationCreateSuccess"),
      });
      setLocationName("");
    } catch {
      notifyStatus({
        type: "error",
        text: t("installationLocationCreateError"),
      });
    }
  };

  const handleOpenAddPositionsToLocation = () => {
    setAddPositionsDialogLocation("");
    setPendingPositionNames([""]);
    setIsAddPositionsToLocationOpen(true);
  };

  const handleCloseAddPositionsToLocation = () => {
    setIsAddPositionsToLocationOpen(false);
  };

  const handleSubmitAddPositions = async () => {
    const trimmedLocation = addPositionsDialogLocation.trim();
    const validNames = pendingPositionNames
      .map((name) => name.trim())
      .filter((name) => name.length > 0);

    if (!trimmedLocation || validNames.length === 0) {
      notifyStatus({ type: "error", text: t("addRecordingPositionsError") });
      return;
    }

    const results = await Promise.allSettled(
      validNames.map((name) =>
        createRecordingPosition({
          tenantName,
          scrapEyeRecordingPositionCreate: {
            name,
            location_name: trimmedLocation,
          },
        }).unwrap()
      )
    );

    const successCount = results.filter(
      (result) => result.status === "fulfilled"
    ).length;
    const failureCount = results.filter(
      (result) => result.status === "rejected"
    ).length;

    if (successCount > 0) {
      notifyStatus({
        type: "success",
        text: t("addRecordingPositionsSuccess", { count: successCount }),
      });
    }
    if (failureCount > 0) {
      const failedNames = validNames.filter(
        (_, index) => results[index]?.status === "rejected"
      );
      setPendingPositionNames(failedNames);
      const errorMessages = results
        .map((result, index) => {
          if (result.status !== "rejected") {
            return null;
          }
          const positionName = validNames[index] ?? `#${index + 1}`;
          return `${positionName}: ${formatPromiseRejectionReason(result.reason)}`;
        })
        .filter((message): message is string => message != null);
      notifyStatus({
        type: "error",
        text:
          t("addRecordingPositionsPartialError", { count: failureCount }) +
          "\n" +
          errorMessages.join("\n"),
      });
    }

    if (failureCount === 0) {
      setIsAddPositionsToLocationOpen(false);
    }
  };

  const renderLocationsSection = () => (
    <Card variant="outlined">
      <CardHeader title={t("installationLocationsTitle")} />
      <Divider />
      <CardContent>
        <Stack spacing={3}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              fullWidth
              label={t("installationLocationNameLabel")}
              value={locationName}
              onChange={(event) => {
                setLocationName(event.target.value);
              }}
              disabled={isCreatingLocation}
            />
            <Button
              variant="contained"
              onClick={() => void handleCreateLocation()}
              disabled={isCreatingLocation}
            >
              {t("add")}
            </Button>
          </Stack>
          {isLocationsBusy ? (
            <Stack alignItems="center">
              <CircularProgress size={24} />
            </Stack>
          ) : sortedInstallationLocations &&
            sortedInstallationLocations.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SortableHeaderCell
                    column="name"
                    sort={locationSort}
                    onSort={toggleLocationSort}
                    sx={{ fontWeight: "fontWeightBold" }}
                  >
                    {t("installationLocationNameLabel")}
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    column="locationType"
                    sort={locationSort}
                    onSort={toggleLocationSort}
                    sx={{ fontWeight: "fontWeightBold" }}
                  >
                    {t("locationType")}
                  </SortableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedInstallationLocations.map((location) => (
                  <TableRow key={location.name}>
                    <TableCell>{location.name}</TableCell>
                    <TableCell>{location.location_type}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography color="text.secondary">
              {t("installationLocationsEmpty")}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  const renderRecordingPositionsSection = () => (
    <Card variant="outlined">
      <CardHeader
        title={t("recordingPositionsTitle")}
        action={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpenAddPositionsToLocation}
            disabled={locationNameOptions.length === 0}
          >
            {t("addRecordingPositionsToLocation")}
          </Button>
        }
      />
      <Divider />
      <CardContent>
        {isRecordingPositionsBusy ? (
          <Stack alignItems="center">
            <CircularProgress size={24} />
          </Stack>
        ) : recordingPositions.length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableHeaderCell
                  column="name"
                  sort={positionSort}
                  onSort={togglePositionSort}
                  sx={{ fontWeight: "fontWeightBold" }}
                >
                  {t("recordingPositionNameLabel")}
                </SortableHeaderCell>
                <SortableHeaderCell
                  column="locationName"
                  sort={positionSort}
                  onSort={togglePositionSort}
                  sx={{ fontWeight: "fontWeightBold" }}
                >
                  {t("installationLocationLabel")}
                </SortableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRecordingPositions.map((position) => (
                <TableRow key={`${position.location_name}-${position.name}`}>
                  <TableCell>{position.name}</TableCell>
                  <TableCell>{position.location_name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography color="text.secondary">
            {t("recordingPositionsEmpty")}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Stack spacing={4} sx={{ p: 2 }}>
      {renderLocationsSection()}
      <SupportingLocationAssociationSection />
      {renderRecordingPositionsSection()}
      <Dialog
        open={isAddPositionsToLocationOpen}
        onClose={handleCloseAddPositionsToLocation}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("addRecordingPositionsToLocation")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              select
              label={t("installationLocationLabel")}
              value={addPositionsDialogLocation}
              onChange={(e) => {
                setAddPositionsDialogLocation(e.target.value);
              }}
              disabled={
                isCreatingRecordingPosition || locationNameOptions.length === 0
              }
            >
              {locationNameOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <Stack spacing={1}>
              {pendingPositionNames.map((name, index) => (
                <Stack
                  // eslint-disable-next-line @eslint-react/no-array-index-key -- order-stable list with no reordering
                  key={index}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <TextField
                    fullWidth
                    size="small"
                    label={t("recordingPositionNameLabel")}
                    value={name}
                    onChange={(e) => {
                      setPendingPositionNames((prev) =>
                        prev.map((n, i) => (i === index ? e.target.value : n))
                      );
                    }}
                    disabled={isCreatingRecordingPosition}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      setPendingPositionNames((prev) =>
                        prev.filter((_, i) => i !== index)
                      );
                    }}
                    disabled={
                      pendingPositionNames.length === 1 ||
                      isCreatingRecordingPosition
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Box>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setPendingPositionNames((prev) => [...prev, ""]);
                }}
                disabled={isCreatingRecordingPosition}
              >
                {t("add")}
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseAddPositionsToLocation}
            disabled={isCreatingRecordingPosition}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              void handleSubmitAddPositions();
            }}
            disabled={
              isCreatingRecordingPosition ||
              !addPositionsDialogLocation ||
              pendingPositionNames.every((n) => !n.trim())
            }
          >
            {t("create")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
